import {mapObject, objectFromEntries} from '@benchling/js-utils';
import {produce} from 'immer';
import React, {useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {POLYMER_SUBTYPE_TO_MODEL_TYPE} from 'constants/MolbioConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import {
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  BulkAssemblyPreferences,
  ConcatenationParameters,
  ConstructUpdate,
  TableConstruct,
  TableConstructFragment,
  MethodData,
  SpecifiedFragmentInfo,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {BA_EMPTY_VALUE, baId, baRawText} from 'molbio/bulk-assembly/BulkAssemblyValues';
import BulkAssemblyConstructsStructuredTableAdapter, {
  BulkAssemblyConstructsStructuredTable,
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata,
} from 'molbio/bulk-assembly/tables/BulkAssemblyConstructsStructuredTableAdapter';
import {
  formatValidationMessages,
  getCellText,
  maybeMakeCellError,
} from 'molbio/bulk-assembly/tables/BulkAssemblyTableUtil';
import getColumnSortingContextMenuOptions from 'notebook/structured-tables/column-sorting/getColumnSortingMenuOptions';
import {StructuredTableColumn, StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableOutsideNotebookWithTableViews} from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {BaseTypedValueType, Cell, TableColumnType} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import preferences from 'Preferences';
import getTestProps from 'test/lib/cypress/getTestProps';
import usePreference from 'ui-library/hooks/usePreference';
import {typeToName} from 'util/NameUtil';
import {omit} from 'util/omitKey';

const NAME_COLUMN_KEY = 'NAME';
const STATUS_COLUMN_KEY = 'STATUS';
const CONSTRUCT_TYPE_COLUMN_KEY = 'CONSTRUCT_TYPE';

function fragmentColumnKey(binCid: string): string {
  return `BIN:${binCid}`;
}

function overhangColumnKey(binCid: string): string {
  return `OVERHANG:${binCid}`;
}

function overlapLengthColumnKey(binCid: string): string {
  return `OVERLAP_LENGTH:${binCid}`;
}

function endpointsColumnKey(binCid: string): string {
  return `ENDPOINTS:${binCid}`;
}

interface ColumnInfo {
  columnKey: string;
  structuredTableColumn: StructuredTableColumn;
}

/**
 * Given a fragment bin, get the construct table columns that should be shown for that bin.
 * @param bin A fragment bin
 * @param fragmentInfosByBinCid The fragments in that bin
 * @param bulkAssemblyMethod The type of bulk assembly being performed
 * @param columnWidths How wide each column should be, in pixels
 * @param assemblyId The API id of the assembly
 * @param index The index of the bin in the assembly
 * @param numBins How many bins are in the assembly
 * @param topology The topology of the constructs
 */
function getColumnsForBin(
  bin: BulkAssemblyFragmentBin,
  fragmentInfosByBinCid: {[binCid: string]: Array<SpecifiedFragmentInfo>},
  bulkAssemblyMethod: BulkAssemblyMethod,
  columnWidths: {[key: string]: number},
  assemblyId: string,
  index: number,
  numBins: number,
  topology: SequenceTopology
): Array<ColumnInfo> {
  const binColumn = {
    columnKey: fragmentColumnKey(bin.cid),
    structuredTableColumn: {
      ...makeStructuredTableColumn({
        name: bin.name,
        columnType: TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR,
        width: columnWidths[fragmentColumnKey(bin.cid)],
      }),
      columnType: TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR,
      fragmentDatas: fragmentInfosByBinCid[bin.cid],
      binColor: bin.color,
      method: bulkAssemblyMethod,
      assemblyId,
    },
  };
  const endpointsColumn = {
    columnKey: endpointsColumnKey(bin.cid),
    structuredTableColumn: makeStructuredTableColumn({
      name: `${bin.name} start / end`,
      columnType: TableColumnType.STRING,
      isReadOnly: true,
      width: columnWidths[endpointsColumnKey(bin.cid)],
    }),
  };
  const overlapColumn = {
    columnKey: overlapLengthColumnKey(bin.cid),
    structuredTableColumn: makeStructuredTableColumn({
      name: 'Overlap length',
      columnType: TableColumnType.STRING,
      isReadOnly: true,
      width: columnWidths[overlapLengthColumnKey(bin.cid)],
    }),
  };
  switch (bulkAssemblyMethod) {
    case BulkAssemblyMethod.GOLDEN_GATE:
      return [
        binColumn,
        {
          columnKey: overhangColumnKey(bin.cid),
          structuredTableColumn: makeStructuredTableColumn({
            name: 'Overhang',
            columnType: TableColumnType.STRING,
            isReadOnly: true,
            width: columnWidths[overhangColumnKey(bin.cid)],
          }),
        },
      ];
    case BulkAssemblyMethod.GIBSON:
      return [
        binColumn,
        // Don't show 3' overlap for the last bin in a linear assembly.
        // We're only looking at fragment bins in this component, so the last bin we look at is always the
        // last fragment bin.
        ...(preferences.get(BulkAssemblyPreferences.SHOW_OVERLAP_DETAILS) &&
        (topology === SequenceTopology.CIRCULAR || index < numBins - 1)
          ? [overlapColumn]
          : []),
      ];
    case BulkAssemblyMethod.HOMOLOGY:
      return [
        binColumn,
        ...(preferences.get(BulkAssemblyPreferences.SHOW_FRAGMENT_DETAILS) ? [endpointsColumn] : []),
        // Don't show 3' overlap for the last bin in a linear assembly.
        // We're only looking at fragment bins in this component, so the last bin we look at is always the
        // last fragment bin.
        ...(preferences.get(BulkAssemblyPreferences.SHOW_OVERLAP_DETAILS) &&
        (topology === SequenceTopology.CIRCULAR || index < numBins - 1)
          ? [overlapColumn]
          : []),
      ];
    default:
      return [binColumn];
  }
}

function getColumns(
  bins: Array<BulkAssemblyFragmentBin>,
  fragmentInfosByBinCid: {[binCid: string]: Array<SpecifiedFragmentInfo>},
  bulkAssemblyMethodData: MethodData,
  columnWidths: {[key: string]: number},
  assemblyId: string,
  topology: SequenceTopology
): Array<ColumnInfo> {
  return [
    {
      columnKey: NAME_COLUMN_KEY,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Name',
        columnType: TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME,
        isReadOnly: true,
        width: columnWidths[NAME_COLUMN_KEY] ?? 240,
      }),
    },
    ...(bulkAssemblyMethodData.type === BulkAssemblyMethod.CONCATENATION
      ? [
          {
            columnKey: CONSTRUCT_TYPE_COLUMN_KEY,
            structuredTableColumn: makeStructuredTableColumn({
              name: 'Construct type',
              columnType: TableColumnType.STRING,
              width: columnWidths[NAME_COLUMN_KEY] ?? 100,
              isReadOnly: true,
            }),
          },
        ]
      : []),
    ...bins.flatMap((bin, index) =>
      getColumnsForBin(
        bin,
        fragmentInfosByBinCid,
        bulkAssemblyMethodData.type,
        columnWidths,
        assemblyId,
        index,
        bins.length,
        topology
      )
    ),
    {
      columnKey: STATUS_COLUMN_KEY,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Status',
        columnType: TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS,
        isReadOnly: true,
        width: columnWidths[STATUS_COLUMN_KEY] ?? 300,
      }),
    },
  ];
}

function isEmptyRow(
  row: StructuredTableRow<BulkAssemblyConstructsStructuredTableRowMetadata>,
  bins: Array<BulkAssemblyFragmentBin>,
  columnIndexesByKey: {[key: string]: number}
): boolean {
  // If a cell is cleared by clicking into it, deleting all the text, and pressing enter, FormulaEditable
  // adds an undefined 'link' attribute for some reason that isCellEmpty doesn't handle.
  return bins.every(({cid}) =>
    TableUtil.isCellEmpty(omit(row.cells[columnIndexesByKey[fragmentColumnKey(cid)]], 'link'))
  );
}

function prepareFragmentCell(tableConstructFragment: TableConstructFragment | null): Cell {
  // An empty cell is a valid (intermediate) state...
  if (tableConstructFragment == null) {
    return {
      text: '',
    };
  } else if (tableConstructFragment.fragmentCid == null) {
    // ...but no identified fragment is not.
    return {
      text: tableConstructFragment?.fragmentName ?? '',
      error: maybeMakeCellError(tableConstructFragment?.error),
    };
  } else {
    return {
      text: '',
      typedValue: {
        type: BaseTypedValueType.CLIENT_IDENTIFIABLE,
        id: tableConstructFragment.fragmentCid,
        name: tableConstructFragment.fragmentName,
      },
    };
  }
}

export interface BulkAssemblyConstructsTableProps {
  /** The API id of the assembly */
  assemblyId: string;
  /** Information about what kind of assembly is being performed, e.g. Golden Gate or Concatenation */
  bulkAssemblyMethodData: MethodData;
  /** A list of fragment bins in the assembly */
  bins: Array<BulkAssemblyFragmentBin>;
  /** A list of constructs to display in the table */
  constructs: Array<TableConstruct>;
  /** A mapping of fragment bins to fragments contained therein */
  fragmentInfosByBinCid: {[binCid: string]: Array<SpecifiedFragmentInfo>};
  /** Whether to disable each of the filters, by validation status */
  isFilterDisabledByStatus: {[key in ValidationStatus]: boolean};
  /** Whether the table should be read-only */
  isReadOnly: boolean;
  /** A function to be called whenever a construct is updated */
  onChangeConstructs: (constructs: Array<ConstructUpdate>) => void;
  /** A function to be called whenever the Autopopulate button is clicked */
  onAutopopulate: () => void;
  /** Callback to invoke when deleting all constructs. */
  onDeleteAll: () => void;
  /** Callback to invoke when deleting filtered constructs. */
  onDeleteFiltered: () => void;
  /** Callback to invoke when deleting a specific range of constructs. */
  onDeleteRange: (startIndex: number, endIndex: number) => void;
  /** A function to be called whenever the 'View Construct' context menu option is selected */
  onViewConstructs: (constructCids: Array<string>) => Promise<void>;
  /** A message to display if the table cannot be autopopulated; disables the Autopopulate button if passed */
  autopopulateButtonError: string | null;
  /** The topology of the constructs */
  topology: SequenceTopology;
  statusFilter: ValidationStatus | null;
  onSetStatusFilter: (newStatusFilter: ValidationStatus | null) => void;
  onReorderConstructs: (reorderedConstructCids: Array<string>) => void;
}

export default function BulkAssemblyConstructsTable({
  assemblyId,
  bulkAssemblyMethodData,
  bins,
  constructs,
  fragmentInfosByBinCid,
  isFilterDisabledByStatus,
  isReadOnly,
  onChangeConstructs,
  onAutopopulate,
  onDeleteAll,
  onDeleteFiltered,
  onDeleteRange,
  onReorderConstructs,
  onViewConstructs,
  statusFilter,
  onSetStatusFilter,
  autopopulateButtonError,
  topology,
}: BulkAssemblyConstructsTableProps): JSX.Element {
  const tableCid = `${assemblyId}-constructsTable`;
  const adapter = BulkAssemblyConstructsStructuredTableAdapter.getAdapter();

  const [columnWidths, setColumnWidths] = useState<{[key: string]: number}>({});
  const columns = getColumns(
    bins,
    fragmentInfosByBinCid,
    bulkAssemblyMethodData,
    columnWidths,
    assemblyId,
    topology
  );
  const columnIndexByKey = objectFromEntries(columns.map((column, i) => [column.columnKey, i]));

  const bulkAssemblyMethod = bulkAssemblyMethodData.type;
  const [showFragmentDetails, setShowFragmentDetails] = usePreference<boolean>(
    BulkAssemblyPreferences.SHOW_FRAGMENT_DETAILS
  );
  const [showOverlapDetails, setShowOverlapDetails] = usePreference<boolean>(
    BulkAssemblyPreferences.SHOW_OVERLAP_DETAILS
  );

  const tableMetadata: BulkAssemblyConstructsStructuredTableMetadata = {
    columns: columns.map((column) => column.structuredTableColumn),
    isFilterDisabledByStatus,
    onAutopopulate,
    onDeleteAll,
    onDeleteFiltered,
    onDeleteRange,
    onViewConstructs,
    autopopulateButtonError,
    isFiltered: statusFilter != null,
    isReadOnly,
    bulkAssemblyMethod,
    showFragmentDetails,
    onToggleFragmentDetails: setShowFragmentDetails,
    showOverlapDetails,
    onToggleOverlapDetails: setShowOverlapDetails,
    statusFilter,
    onSetStatusFilter,
    tableRowsName: 'constructs',
  };

  const constructsTable = adapter.createTable({
    // "name" is the table subtitle, which we omit. The actual title is defined in the adapter.
    name: '',
    metadata: tableMetadata,
    entryId: null,
    schemas: [],
    selectorsById: {},
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  });
  // The provenance UUID must stay consistent between renders, so use the tableCid
  constructsTable.provenanceUuid = tableCid;

  function buildStructuredTableRows(
    constructDatas: Array<TableConstruct>
  ): Array<StructuredTableRow<BulkAssemblyConstructsStructuredTableRowMetadata>> {
    return constructDatas.map((construct: TableConstruct, rowIndex: number) => {
      const row: StructuredTableRow<BulkAssemblyConstructsStructuredTableRowMetadata> = adapter.getBlankRow(
        tableMetadata,
        rowIndex,
        constructsTable.columns,
        {constructCid: construct.cid, canPreview: construct.canPreview}
      );
      return produce(row, (draft) => {
        if (construct.createdSequenceId) {
          draft.cells[columnIndexByKey[NAME_COLUMN_KEY]] = {
            text: '',
            typedValue: {
              type: BaseTypedValueType.BIOENTITY,
              bioentitySummary: {name: construct.name, id: construct.createdSequenceId},
            },
          };
        } else {
          draft.cells[columnIndexByKey[NAME_COLUMN_KEY]] = {text: construct.name};
        }
        for (const bin of bins) {
          const displayConstructFragment = construct.tableConstructFragmentByBinId[bin.cid];
          draft.cells[columnIndexByKey[fragmentColumnKey(bin.cid)]] =
            prepareFragmentCell(displayConstructFragment);
          if (columnIndexByKey.hasOwnProperty(overhangColumnKey(bin.cid))) {
            draft.cells[columnIndexByKey[overhangColumnKey(bin.cid)]] = {
              text: displayConstructFragment?.overhangBases ?? '',
            };
          }
          if (columnIndexByKey.hasOwnProperty(endpointsColumnKey(bin.cid))) {
            draft.cells[columnIndexByKey[endpointsColumnKey(bin.cid)]] = {
              text: `${displayConstructFragment?.fragmentEndpoints ?? ''}`,
            };
          }
          if (columnIndexByKey.hasOwnProperty(overlapLengthColumnKey(bin.cid))) {
            draft.cells[columnIndexByKey[overlapLengthColumnKey(bin.cid)]] = {
              text: displayConstructFragment?.overlapLength ?? '',
            };
          }
        }
        if (columnIndexByKey.hasOwnProperty(CONSTRUCT_TYPE_COLUMN_KEY)) {
          const parameters = bulkAssemblyMethodData.parameters as ConcatenationParameters;
          draft.cells[columnIndexByKey[CONSTRUCT_TYPE_COLUMN_KEY]] = {
            text: typeToName(POLYMER_SUBTYPE_TO_MODEL_TYPE[parameters.polymerSubtype]),
          };
        }
        draft.cells[columnIndexByKey[STATUS_COLUMN_KEY]] = {
          text: formatValidationMessages(construct.validationMessages, isReadOnly),
        };
      });
    });
  }

  if (constructs.length === 0) {
    constructsTable.rows = buildStructuredTableRows([
      {
        cid: uuidv4(),
        createdSequenceId: null,
        name: '',
        constructFragmentsByBinId: objectFromEntries(
          bins.map((bin) => [bin.cid, {fragment: BA_EMPTY_VALUE}])
        ),
        tableConstructFragmentByBinId: objectFromEntries(
          bins.map((bin) => [
            bin.cid,
            {
              fragmentCid: null,
              fragmentName: '',
              fragmentEndpoints: null,
              overhangBases: '',
              overlapLength: null,
            },
          ])
        ),
        validationMessages: [],
        canPreview: false,
      },
    ]);
  } else {
    constructsTable.rows = buildStructuredTableRows(constructs);
  }

  function handleConstructsTableChange(newConstructsTable: BulkAssemblyConstructsStructuredTable): void {
    setColumnWidths(mapObject(columnIndexByKey, (index) => newConstructsTable.columns[index].width));
    if (newConstructsTable.rows.length < constructsTable.rows.length) {
      analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.DELETED_BULK_ASSEMBLY_CONSTRUCT, {
        actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
        cloningMethod: bulkAssemblyMethod,
        assemblyId,
        numRowsDeleted: constructsTable.rows.length - newConstructsTable.rows.length,
      });
    }
    if (
      newConstructsTable.rows.length === 1 &&
      isEmptyRow(newConstructsTable.rows[0], bins, columnIndexByKey)
    ) {
      onChangeConstructs([]);
    } else {
      onChangeConstructs(
        newConstructsTable.rows.map((row) => ({
          cid: row.metadata.constructCid,
          constructFragmentsByBinId: objectFromEntries(
            bins.map((bin) => {
              const binCell = row.cells[columnIndexByKey[fragmentColumnKey(bin.cid)]];
              const fragmentCid =
                binCell.typedValue != null &&
                binCell.typedValue.type === BaseTypedValueType.CLIENT_IDENTIFIABLE
                  ? binCell.typedValue.id
                  : null;
              let fragmentBaTypedValue;
              if (
                fragmentCid != null &&
                fragmentInfosByBinCid[bin.cid]?.find(
                  (fragmentInfo) => fragmentInfo.fragmentCid === fragmentCid
                ) != null
              ) {
                fragmentBaTypedValue = baId(fragmentCid);
              } else {
                fragmentBaTypedValue = baRawText(getCellText(binCell));
              }
              return [bin.cid, {fragment: fragmentBaTypedValue}];
            })
          ),
        }))
      );
    }
  }

  const structuredTable = {...constructsTable, cid: tableCid};

  return (
    <div {...getTestProps({component: 'BulkAssemblyConstructsTable', element: 'root'})}>
      <StructuredTableOutsideNotebookWithTableViews
        structuredTable={structuredTable}
        cid={tableCid}
        updateHandler={handleConstructsTableChange}
        includeUnsubmittedStatus={false}
        hideColumnHeaderIcons={true}
        locked={isReadOnly}
        canRename={false}
        isFixedHeight={true}
        getExtraColumnContextMenuOptionsForStructuredTable={(selectionRange, table, updateColumnConstraint) =>
          getColumnSortingContextMenuOptions(
            selectionRange,
            null,
            table,
            undefined,
            structuredTable,
            isReadOnly,
            (sortedRowIndices) => {
              const constructCids = sortedRowIndices.map(
                (index) => constructsTable.rows[index].metadata.constructCid
              );
              assert(!constructCids.includes(null), 'Invalid row indices received');
              onReorderConstructs(constructCids as Array<string>);
            },
            null
          )
        }
      />
    </div>
  );
}
